import { Box, Button, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import axiosInstant from "../../../../api";
import { getFileTranscript } from "../../../../api/endpoints";
import { IconAttachment, IconPage } from "../../../../utils/icons";
import { TabHeader, TabPanel } from "../../../../components/Tabs";
import { DotLottleLoader } from "../../../../components/Loader";
import { TranscriptCueEditor } from "./TranscriptCueEditor";
import { TranscriptVideoPreview, TranscriptVideoPreviewHandle } from "./TranscriptVideoPreview";
import { resolveAndFetchTranscriptFile } from "./helpers";
import { PanelFooter } from "./PanelFooter";

interface Props {
    video: any;
}

interface TranscriptStatus {
    hasTranscript: boolean;
    transcriptStatus: string;
}

function TranscriptFileView({
    content,
    loading,
    emptyMessage,
    type
}: {
    content: string | null;
    loading: boolean;
    emptyMessage: string;
    type: string;
}) {
    const [isCopied, setIsCopied] = useState(false);

    const onCopy = () => {
        if (!content) return;
        navigator.clipboard.writeText(content);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-full py-12">
                <DotLottleLoader fill={false} sx={{ width: 56, height: 56 }} />
            </div>
        );
    }
    if (!content) {
        return (
            <div className="flex justify-center items-center h-full py-12">
                <Typography color="textSecondary">{emptyMessage}</Typography>
            </div>
        );
    }
    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <Box sx={{ py: 3, display: "flex", flexDirection: "column", gap: 2, overflow: "auto", flex: 1 }}>
                <Typography variant="subtitle2" className="text-gray-600">
                    {type} File
                </Typography>
                <Box
                    sx={{
                        p: 2,
                        bgcolor: "var(--surface-2)",
                        borderRadius: "8px",
                        overflow: "auto",
                        maxHeight: "400px",
                    }}
                >
                    <pre
                        style={{
                            wordWrap: "break-word",
                            whiteSpace: "pre-wrap",
                            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                            fontSize: "13px",
                            lineHeight: "1.6",
                            margin: 0,
                        }}
                    >
                        {content}
                    </pre>
                </Box>
            </Box>
            <PanelFooter>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={onCopy}
                    endIcon={<IconAttachment stroke="#fff" />}
                    sx={{ textTransform: "capitalize" }}
                >
                    {isCopied ? "Copied!" : `Copy ${type} File`}
                </Button>
            </PanelFooter>
        </Box>
    );
}

export function TranscriptionVideoPanel({ video }: Props) {
    const [selectedTab, setSelectedTab] = useState(0);
    const [transcriptData, setTranscriptData] = useState<string | null>(null);
    const [transcriptStatus, setTranscriptStatus] = useState<TranscriptStatus>({
        hasTranscript: false,
        transcriptStatus: "unknown"
    });
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>("");
    const [srtContent, setSrtContent] = useState<string | null>(null);
    const [vttContent, setVttContent] = useState<string | null>(null);
    const [srtKey, setSrtKey] = useState<string | null>(null);
    const [vttKey, setVttKey] = useState<string | null>(null);
    const [srtLoading, setSrtLoading] = useState<boolean>(false);
    const [vttLoading, setVttLoading] = useState<boolean>(false);
    const previewRef = useRef<TranscriptVideoPreviewHandle | null>(null);
    const [currentTime, setCurrentTime] = useState<number>(0);

    const fetchData = async () => {
        if (!video?.id) {
            setError("No video ID available.");
            setLoading(false);
            return;
        }

        setLoading(true);
        setError("");

        try {
            const response = await axiosInstant.get(getFileTranscript(video.id));
            const data = response.data;

            if (data) {
                setTranscriptData(data.transcript || null);
                setTranscriptStatus({
                    hasTranscript: data.meta?.hasTranscript ?? !!data.transcript,
                    transcriptStatus: data.meta?.transcriptStatus || "unknown"
                });
            } else {
                setError("No data available for this video.");
            }
        } catch (err: any) {
            setError(err?.response?.data?.message || "Failed to fetch data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [video?.id]);

    useEffect(() => {
        let cancelled = false;

        const fetchTranscriptFile = async (
            ext: "srt" | "vtt",
            setBusy: (b: boolean) => void,
            setContent: (c: string | null) => void,
            setKey: (k: string | null) => void
        ) => {
            setBusy(true);
            try {
                const result = await resolveAndFetchTranscriptFile(video, ext);
                if (cancelled) return;
                if (result) {
                    setContent(result.content);
                    setKey(result.key);
                } else {
                    setContent(null);
                    setKey(null);
                }
            } finally {
                if (!cancelled) setBusy(false);
            }
        };

        fetchTranscriptFile("srt", setSrtLoading, setSrtContent, setSrtKey);
        fetchTranscriptFile("vtt", setVttLoading, setVttContent, setVttKey);

        return () => {
            cancelled = true;
        };
    }, [video?.id, video?.hash, video?.version]);

    const tabHeader = [
        {
            value: 0,
            text: "Transcript",
            Icon: IconPage
        },
        {
            value: 1,
            text: "SRT",
            Icon: IconPage
        },
        {
            value: 2,
            text: "VTT",
            Icon: IconPage
        }
    ];

    if (loading) {
        return <DotLottleLoader fill={false} />;
    }

    if (error) {
        return (
            <div className="flex flex-col gap-2 justify-center items-center h-full py-12">
                <Typography color="error">{error}</Typography>
            </div>
        );
    }

    return (
        <Box
            className="h-full"
            sx={{
                display: "flex",
                gap: 3,
                flexDirection: { xs: "column", md: "row" },
                alignItems: "stretch",
            }}
        >
            <Box className="flex flex-col min-h-0" sx={{ flex: { md: "1 1 0" }, minWidth: 0 }}>
                <div className="shrink-0">
                    <TabHeader tabs={tabHeader} selectedTab={selectedTab} setSelectedTab={setSelectedTab} variant="pill" />
                </div>
                <TabPanel value={selectedTab} index={0} className="flex-1 min-h-0">
                    <TranscriptCueEditor
                        video={video}
                        srtContent={srtContent}
                        vttContent={vttContent}
                        srtKey={srtKey}
                        vttKey={vttKey}
                        dbTranscript={transcriptData}
                        transcriptStatus={transcriptStatus.transcriptStatus}
                        loading={srtLoading || vttLoading}
                        onTranscriptRequested={fetchData}
                        currentTime={currentTime}
                        onSeek={(seconds) => previewRef.current?.seekTo(seconds)}
                        onSaved={({ srt, vtt, transcript }) => {
                            setSrtContent(srt);
                            setVttContent(vtt);
                            setTranscriptData(transcript);
                        }}
                    />
                </TabPanel>
                <TabPanel value={selectedTab} index={1} className="flex-1 min-h-0">
                    <TranscriptFileView
                        content={srtContent}
                        loading={srtLoading}
                        emptyMessage="No .srt transcript available for this video."
                        type=".srt"
                    />
                </TabPanel>
                <TabPanel value={selectedTab} index={2} className="flex-1 min-h-0">
                    <TranscriptFileView
                        content={vttContent}
                        loading={vttLoading}
                        emptyMessage="No .vtt transcript available for this video."
                        type=".vtt"
                    />
                </TabPanel>
            </Box>

            <Box
                sx={{
                    flex: { md: "0 0 380px" },
                    width: { xs: "100%", md: 380 },
                    alignSelf: "flex-start",
                    position: { md: "sticky" },
                    top: { md: 0 },
                }}
            >
                <TranscriptVideoPreview
                    ref={previewRef}
                    video={video}
                    onTimeUpdate={setCurrentTime}
                />
            </Box>
        </Box>
    );
}
